<template>
  <div>

    <future-alert :end="end" />

    <v-row class="mb-1">
      <v-col cols="12" sm="6" md="4" lg="3" xl="2">
        <kpi-tile
          :title="$t('titles.parcs_revenue')"
          :current="revenueTotals.parcs_revenue"
          :previous="null"
          format="currency"
        />
      </v-col>
      <v-col cols="12" sm="6" md="4" lg="3" xl="2">
        <kpi-tile
          :title="$t('titles.reservation_revenue')"
          :current="revenueTotals.reservation_revenue"
          :previous="null"
          format="currency"
        />
      </v-col>
      <v-col cols="12" sm="6" md="4" lg="3" xl="2">
        <kpi-tile
          :title="$t('titles.total_revenue')"
          :current="revenueTotals.total_revenue"
          :previous="null"
          format="currency"
        />
      </v-col>
    </v-row>

    <v-data-table-server
      :items-per-page="25"
      :headers="headers"
      :items="tableRows"
      :items-length="tableRows.length"
      :loading="loading"
      @update:options="loadItems"
      :search="tableSearch"
    >

      <template v-slot:item.hour="{ value }">
        {{ (value as Temporal.PlainTime).toLocaleString([], {timeStyle: 'short'}) }}
      </template>

      <template v-slot:item.parcs_revenue="{ value }">
        {{ currencyFormatter.format(value) }}
      </template>

      <template v-slot:item.reservation_revenue="{ value }">
        {{ currencyFormatter.format(value) }}
      </template>

      <template v-slot:item.total_revenue="{ value }">
        {{ currencyFormatter.format(value) }}
      </template>

      <template v-slot:item.inventory_percentage="{ value }">
        {{ percentageFormatter.format(value / 100) }}
      </template>

      <template v-slot:item.revpas="{ value }">
        {{ currencyFormatter.format(value) }}
      </template>

      <template v-slot:bottom>
        <!-- This is just here to hide the pagination controls. -->
      </template>

    </v-data-table-server>

  </div>
</template>

<script setup lang="ts">

import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTitle } from '@vueuse/core'

import FutureAlert from './reports/FutureAlert.vue'
import KpiTile from './reports/KpiTile.vue'

import { currencyFormatter, percentageFormatter } from '../../../utils/formatters'
import NotificationService from '../../../services/NotificationService'
import { ReportingApi } from '../../../services/api-clients/ReportingApi'
import type { VDataTableServerHeader } from '../../../types/vuetify'
import { Temporal } from '@js-temporal/polyfill'

interface Props {
  locationIds: number[]
  channels: string[]
  start: null | Temporal.PlainDate
  end: null | Temporal.PlainDate
}

const props = withDefaults(defineProps<Props>(), {
  channels: () => [],
})

const { t } = useI18n()
const title = useTitle()

title.value = t('titles.reporting_daily_revenue')

/**
 * Table headers
 */

const headers: VDataTableServerHeader[] = [
  {key: 'hour', title: t('titles.hour'), align: 'end', sortable: false},
  {key: 'parcs_revenue', title: t('titles.parcs_revenue'), align: 'end', sortable: false},
  {key: 'parcs_inventory', title: t('titles.parcs_inventory'), align: 'end', sortable: false},
  {key: 'reservation_revenue', title: t('titles.reservation_revenue'), align: 'end', sortable: false},
  {key: 'reservation_inventory', title: t('titles.reservation_inventory'), align: 'end', sortable: false},
  {key: 'total_revenue', title: t('titles.total_revenue'), align: 'end', sortable: false},
  {key: 'total_inventory', title: t('titles.total_occupancy'), align: 'end', sortable: false},
  {key: 'inventory_percentage', title: t('titles.occupancy_pct'), align: 'end', sortable: false},
  {key: 'revpas', title: t('titles.revpas'), align: 'end', sortable: false},
]

/**
 * Table data
 */

interface TableRow {
  hour: Temporal.PlainTime
  parcs_revenue: number
  parcs_inventory: number
  reservation_revenue: number
  reservation_inventory: number
  total_revenue: number
  total_inventory: number
  inventory_percentage: number
  revpas: number
}

const loading = ref<boolean>(false)
const tableRows = ref<TableRow[]>([])
// sort of a kludge. updating this value forces the table to reload data from the server. it doesn't actually have anything to do with search.
const tableSearch = ref<undefined | string>(undefined)

// Totals row: sum the revenue columns across the hourly rows. Occupancy/percentage/revpas
// are point-in-time or ratio metrics and are not additive, so they are left blank.
const revenueTotals = computed(() => ({
  parcs_revenue: tableRows.value.reduce((sum, row) => sum + row.parcs_revenue, 0),
  reservation_revenue: tableRows.value.reduce((sum, row) => sum + row.reservation_revenue, 0),
  total_revenue: tableRows.value.reduce((sum, row) => sum + row.total_revenue, 0),
}))

function loadItems(_options: any) {

  if (props.locationIds.length === 0 || !props.start || !props.end) {
    return
  }

  loading.value = true

  ReportingApi
    .getDailyRevenue({
      locationIds: props.locationIds,
      channels: props.channels,
      start: props.start,
      end: props.end,
    }).then((result) => {
      tableRows.value.length = 0
      Object.entries(result.metrics).forEach(([hour, metric]) => {
        tableRows.value.push({
          hour: Temporal.PlainTime.from(hour),
          parcs_revenue: metric.parcs_revenue_cents / 100,
          parcs_inventory: metric.parcs_inventory,
          reservation_revenue: metric.reservation_revenue_cents / 100,
          reservation_inventory: metric.reservation_inventory,
          total_revenue: metric.total_revenue_cents / 100,
          total_inventory: metric.total_inventory,
          inventory_percentage: metric.inventory_percentage,
          revpas: metric.revpas_cents / 100,
        })
      })
    })
    .catch((e) => {
      console.error(e)
      NotificationService.error('Couldn\'t load data!')
    })
    .finally(() => {
      loading.value = false
    })

}

watch([
  () => props.locationIds,
  () => props.channels,
  () => props.start,
  () => props.end,
], () => {
  tableSearch.value = Math.random().toString() // force a reload
})

</script>
